
import { useState } from 'react';

interface AuditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  nom_cabinet: string;
  nom_contact: string;
  email: string;
  telephone: string;
  nombre_collaborateurs: string;
  nombre_dossiers_annuel: string;
  logiciel_comptable: string;
  type_clients: string[];
  problemes_actuels: string[];
  objectifs_audit: string;
  delai_souhaite: string;
  budget_envisage: string;
  message: string;
}

export default function AuditModal({ isOpen, onClose }: AuditModalProps) {
  const [formData, setFormData] = useState<FormData>({
    nom_cabinet: '',
    nom_contact: '',
    email: '',
    telephone: '',
    nombre_collaborateurs: '',
    nombre_dossiers_annuel: '',
    logiciel_comptable: '',
    type_clients: [],
    problemes_actuels: [],
    objectifs_audit: '',
    delai_souhaite: '',
    budget_envisage: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const collaborateurs = [
    '1-2 collaborateurs',
    '3-5 collaborateurs',
    '6-10 collaborateurs',
    '11-20 collaborateurs',
    'Plus de 20 collaborateurs'
  ];

  const dossiers = [
    'Moins de 50 dossiers',
    '50-100 dossiers',
    '101-200 dossiers',
    '201-500 dossiers',
    'Plus de 500 dossiers'
  ];

  const logiciels = [
    'Sage',
    'Cegid',
    'EBP',
    'QuadraCompta',
    'Coala',
    'ACD',
    'Silae',
    'Autre'
  ];

  const typesClients = [
    'TPE (0-9 salariés)',
    'PME (10-249 salariés)',
    'ETI (250-4999 salariés)',
    'Professions libérales',
    'Associations',
    'Artisans/Commerçants'
  ];

  const problemesActuels = [
    'Surcharge de travail',
    'Retards dans les déclarations',
    'Difficultés de recrutement',
    'Coûts trop élevés',
    'Qualité insuffisante',
    'Manque de spécialisation',
    'Gestion des délais',
    'Relation client dégradée'
  ];

  const delais = [
    'Immédiat (moins d\'1 mois)',
    'Court terme (1-3 mois)',
    'Moyen terme (3-6 mois)',
    'Long terme (6-12 mois)',
    'À définir'
  ];

  const budgets = [
    'Moins de 5 000€/mois',
    '5 000€ - 10 000€/mois',
    '10 000€ - 20 000€/mois',
    '20 000€ - 50 000€/mois',
    'Plus de 50 000€/mois',
    'À définir selon l\'audit'
  ];

  const handleTypeClientChange = (type: string) => {
    setFormData(prev => ({
      ...prev,
      type_clients: prev.type_clients.includes(type)
        ? prev.type_clients.filter(t => t !== type)
        : [...prev.type_clients, type]
    }));
  };

  const handleProblemeChange = (probleme: string) => {
    setFormData(prev => ({
      ...prev,
      problemes_actuels: prev.problemes_actuels.includes(probleme)
        ? prev.problemes_actuels.filter(p => p !== probleme)
        : [...prev.problemes_actuels, probleme]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('nom_cabinet', formData.nom_cabinet);
      formDataToSubmit.append('nom_contact', formData.nom_contact);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('telephone', formData.telephone);
      formDataToSubmit.append('nombre_collaborateurs', formData.nombre_collaborateurs);
      formDataToSubmit.append('nombre_dossiers_annuel', formData.nombre_dossiers_annuel);
      formDataToSubmit.append('logiciel_comptable', formData.logiciel_comptable);
      formDataToSubmit.append('type_clients', formData.type_clients.join(', '));
      formDataToSubmit.append('problemes_actuels', formData.problemes_actuels.join(', '));
      formDataToSubmit.append('objectifs_audit', formData.objectifs_audit);
      formDataToSubmit.append('delai_souhaite', formData.delai_souhaite);
      formDataToSubmit.append('budget_envisage', formData.budget_envisage);
      formDataToSubmit.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d304397mrrhjc90busrg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitMessage(
          "Votre demande d'audit a été envoyée avec succès ! Notre expert vous contactera sous 24h pour programmer votre audit personnalisé."
        );
        setFormData({
          nom_cabinet: '',
          nom_contact: '',
          email: '',
          telephone: '',
          nombre_collaborateurs: '',
          nombre_dossiers_annuel: '',
          logiciel_comptable: '',
          type_clients: [],
          problemes_actuels: [],
          objectifs_audit: '',
          delai_souhaite: '',
          budget_envisage: '',
          message: ''
        });
      } else {
        setSubmitMessage("Erreur lors de l'envoi. Veuillez réessayer.");
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitMessage("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Audit gratuit de votre cabinet</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {submitMessage && (
            <div
              className={`mb-6 p-4 rounded-lg ${
                submitMessage.includes('succès')
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {submitMessage}
            </div>
          )}

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <div className="w-8 h-8 flex items-center justify-center mr-4">
                <i className="ri-search-2-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-blue-800 mb-3">Votre audit gratuit comprend :</h3>
                <div className="grid md:grid-cols-2 gap-3 text-sm text-blue-700">
                  <div className="flex items-center">
                    <i className="ri-check-line mr-2"></i>
                    Analyse de votre organisation actuelle
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line mr-2"></i>
                    Identification des axes d'amélioration
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line mr-2"></i>
                    Calcul du potentiel d'économies
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line mr-2"></i>
                    Plan d'action personnalisé
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line mr-2"></i>
                    Proposition de solutions adaptées
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line mr-2"></i>
                    Rapport détaillé sous 48h
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form id="audit-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du cabinet *
                </label>
                <input
                  type="text"
                  name="nom_cabinet"
                  value={formData.nom_cabinet}
                  onChange={(e) => setFormData(prev => ({ ...prev, nom_cabinet: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du contact *
                </label>
                <input
                  type="text"
                  name="nom_contact"
                  value={formData.nom_contact}
                  onChange={(e) => setFormData(prev => ({ ...prev, nom_contact: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={(e) => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de collaborateurs *
                </label>
                <select
                  name="nombre_collaborateurs"
                  value={formData.nombre_collaborateurs}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre_collaborateurs: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  required
                >
                  <option value="">Sélectionnez</option>
                  {collaborateurs.map((collab) => (
                    <option key={collab} value={collab}>{collab}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de dossiers/an *
                </label>
                <select
                  name="nombre_dossiers_annuel"
                  value={formData.nombre_dossiers_annuel}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre_dossiers_annuel: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  required
                >
                  <option value="">Sélectionnez</option>
                  {dossiers.map((dossier) => (
                    <option key={dossier} value={dossier}>{dossier}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logiciel comptable principal *
              </label>
              <select
                name="logiciel_comptable"
                value={formData.logiciel_comptable}
                onChange={(e) => setFormData(prev => ({ ...prev, logiciel_comptable: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                required
              >
                <option value="">Sélectionnez votre logiciel</option>
                {logiciels.map((logiciel) => (
                  <option key={logiciel} value={logiciel}>{logiciel}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Types de clients * (plusieurs choix possibles)
              </label>
              <div className="grid md:grid-cols-3 gap-3">
                {typesClients.map((type) => (
                  <label key={type} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.type_clients.includes(type)}
                      onChange={() => handleTypeClientChange(type)}
                      className="mr-3 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Problèmes actuels * (plusieurs choix possibles)
              </label>
              <div className="grid md:grid-cols-2 gap-3">
                {problemesActuels.map((probleme) => (
                  <label key={probleme} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.problemes_actuels.includes(probleme)}
                      onChange={() => handleProblemeChange(probleme)}
                      className="mr-3 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{probleme}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Délai souhaité *
                </label>
                <select
                  name="delai_souhaite"
                  value={formData.delai_souhaite}
                  onChange={(e) => setFormData(prev => ({ ...prev, delai_souhaite: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  required
                >
                  <option value="">Sélectionnez</option>
                  {delais.map((delai) => (
                    <option key={delai} value={delai}>{delai}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Budget envisagé
                </label>
                <select
                  name="budget_envisage"
                  value={formData.budget_envisage}
                  onChange={(e) => setFormData(prev => ({ ...prev, budget_envisage: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                >
                  <option value="">Sélectionnez</option>
                  {budgets.map((budget) => (
                    <option key={budget} value={budget}>{budget}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Objectifs principaux de l'audit
              </label>
              <textarea
                name="objectifs_audit"
                value={formData.objectifs_audit}
                onChange={(e) => setFormData(prev => ({ ...prev, objectifs_audit: e.target.value }))}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Ex: Réduire les coûts, optimiser l'organisation, améliorer la qualité..."
              />
              <p className="text-xs text-gray-500 mt-1">{formData.objectifs_audit.length}/500 caractères</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Informations complémentaires
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Autres informations utiles pour l'audit..."
              />
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 caractères</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={isSubmitting || formData.type_clients.length === 0 || formData.problemes_actuels.length === 0}
                className="flex-1 bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Demander mon audit gratuit'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
